import {MinusIcon, PlusIcon} from '@heroicons/react/outline';
import {useState} from 'react';

import {Badge, Button, Table, TBody, Td, Th, Thead, Tr} from '@/components';
import {useCountryList} from '@/context/CountryListContext';
import {mapCountryListToOptions} from '@/utils/countries';

import type {AfriexSubscriptionPlan} from './types';

const SubscriptionPlanTable = ({
  items,
  isLoading,
  setSelectedPlan,
  setShowUpdateModal,
}: {
  items: AfriexSubscriptionPlan[];
  isLoading: boolean;
  setSelectedPlan: (v: AfriexSubscriptionPlan) => void;
  setShowUpdateModal: (v: boolean) => void;
}) => {
  return (
    <Table className="overflow-hidden">
      <Thead>
        <Tr enableHover={false}>
          <Th className="w-20">&nbsp;</Th>
          <Th className="ps-5">Name</Th>
          <Th>Amount</Th>
          <Th>Currency</Th>
          <Th>Interval</Th>
          <Th>Status</Th>
        </Tr>
      </Thead>
      <TBody>
        {items.map((item, index) => (
          <TRow
            isLoading={isLoading}
            key={index}
            item={item}
            index={index}
            setSelectedPlan={setSelectedPlan}
            setShowUpdateModal={setShowUpdateModal}
          />
        ))}
      </TBody>
    </Table>
  );
};

const TRow = ({
  item,
  index,
  setSelectedPlan,
  setShowUpdateModal,
}: {
  item: AfriexSubscriptionPlan;
  index: number;
  isLoading: boolean;
  setSelectedPlan: (v: AfriexSubscriptionPlan) => void;
  setShowUpdateModal: (v: boolean) => void;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const badgeStatus = item.isActive ? 'successful' : 'failed';
  const badgeLabel = item.isActive ? 'Active' : 'Inactive';
  const zebraRow = index % 2 === 0 ? '' : 'bg-gray-50 dark:bg-dark-zebraRow';

  return (
    <>
      <Tr className={zebraRow}>
        <Td isNumeric>
          <Button onClick={() => setIsExpanded(!isExpanded)} variant="unStyled">
            <div
              className={`border-2 border-gray-700 dark:border-dark-borderLight w-5 h-5 flex justify-center rounded hover:bg-cyan-50 ${
                isExpanded ? 'bg-cyan-50' : ''
              }`}
            >
              {isExpanded ? (
                <MinusIcon className="w-4 h-4 text-gray-700 dark:text-gray-100" />
              ) : (
                <PlusIcon className="w-4 h-4 text-gray-700 dark:text-gray-100" />
              )}
            </div>
          </Button>
        </Td>
        <Td className={`font-medium ps-5`}>{item.name}</Td>
        <Td>{item.amount}</Td>
        <Td>{item.currency}</Td>
        <Td>{item.interval}</Td>

        <Td>
          <Badge label={badgeLabel} colorScheme={badgeStatus} />
        </Td>
      </Tr>
      {isExpanded && (
        <Tr enableHover={false}>
          <Td colSpan={9} className="p-0 m-0 whitespace-nowrap">
            <div className="flex py-10 px-8 justify-center bg-sky-50 dark:bg-transparent w-full">
              <SubscriptionPlanSummary
                item={item}
                setSelectedPlan={setSelectedPlan}
                setShowUpdateModal={setShowUpdateModal}
              />
            </div>
          </Td>
        </Tr>
      )}
    </>
  );
};

const PlanContent = ({title, value}: {title: string; value?: string}) => {
  return (
    <div className="mb-2 py-2 px-3 flex items-start gap-3 bg-gray-100 justify-between dark:bg-gray-900">
      <p className="text-slate-500 text-sm dark:text-slate-100">{title}</p>
      <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100 break-words whitespace-pre-wrap">
        {value || 'N/A'}
      </p>
    </div>
  );
};

const SubscriptionPlanSummary = ({
  item,
  setSelectedPlan,
  setShowUpdateModal,
}: {
  item: AfriexSubscriptionPlan;
  setSelectedPlan: (v: AfriexSubscriptionPlan) => void;
  setShowUpdateModal: (v: boolean) => void;
}) => {
  const {state: countryListState} = useCountryList();
  const {countryList} = countryListState;
  const countryOptions = mapCountryListToOptions(countryList);

  const features = (item.features ?? [])
    .map((f) =>
      f
        .replaceAll('_', ' ')
        .split(' ')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(' '),
    )
    .join(', ');

  const countries = (item.countries ?? [])
    .map((code) => countryOptions[code])
    .filter(Boolean);

  return (
    <div className="bg-white dark:bg-transparent rounded-lg w-full px-5 sm:px-5 py-5 sm:py-5">
      <div className="flex items-center gap-4 mb-3">
        <h2 className="font-semibold text-xl mr-3">
          Subscription Plan Details
        </h2>
      </div>

      <div className="flex flex-col">
        <PlanContent title="Name" value={item.name} />
        <PlanContent title="Description" value={item.description} />
        <PlanContent title="Features" value={features} />
        <PlanContent title="Countries" value={countries?.join(', ')} />
        <div className="w-full text-end">
          <Button
            variant="solid"
            className="w-20"
            onClick={() => {
              setSelectedPlan(item);
              setShowUpdateModal(true);
            }}
          >
            Manage
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPlanTable;
